import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Cancel, Delete, Save } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { reelMakerApi } from 'components/ReelMaker/ReelMaker.api';
import { Reel, ReelVersion } from 'components/ReelMaker/ReelMaker.types';
import { Logger } from 'utils/Logger';
import { Playlist, PlaylistUpsertPayload, formatDuration } from './Playlists.types';
import { PlaylistPreviewDialog, PreviewEntry } from './PlaylistPreviewDialog';

const LOG_PREFIX = 'BeamOnScreen/playlists/PlaylistEditorDialog';

/** Item while editing; `versions` is loaded on demand for the version dropdown. */
interface DraftItem {
    key: string;
    itemId?: string;
    reelId: string;
    reelName: string;
    version: number;
    durationMs: number;
    reelStatus: string;
    missing: boolean;
}

export interface PlaylistEditorDialogProps {
    open: boolean;
    playlist: Playlist | null;
    saving: boolean;
    error: string | null;
    canManage: boolean;
    onClose: () => void;
    onSave: (payload: PlaylistUpsertPayload) => void;
}

let keySeq = 0;
const nextKey = () => `k${Date.now().toString(36)}${(keySeq++).toString(36)}`;

/**
 * Create / edit a playlist: name, description and an ordered list of published reel versions.
 * Order is changed by drag-and-drop or the arrow buttons; each item has its own version dropdown.
 */
export const PlaylistEditorDialog: React.FC<PlaylistEditorDialogProps> = ({ open, playlist, saving, error, canManage, onClose, onSave }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [items, setItems] = useState<DraftItem[]>([]);
    const [versionsByReel, setVersionsByReel] = useState<Record<string, ReelVersion[]>>({});
    const [pickerOpen, setPickerOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [dragKey, setDragKey] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setName(playlist?.name ?? '');
        setDescription(playlist?.description ?? '');
        setNameError(null);
        setItems(
            (playlist?.items ?? []).map((i) => ({
                key: nextKey(),
                itemId: i.itemId,
                reelId: i.reelId,
                reelName: i.reelName,
                version: i.version,
                durationMs: i.durationMs,
                reelStatus: i.reelStatus,
                missing: i.missing,
            })),
        );
    }, [open, playlist]);

    const loadVersions = async (reelId: string) => {
        if (versionsByReel[reelId]) return versionsByReel[reelId];
        try {
            const res = await reelMakerApi.listReelVersions(reelId);
            setVersionsByReel((m) => ({ ...m, [reelId]: res.versions }));
            return res.versions;
        } catch (err) {
            Logger.error(LOG_PREFIX, `listReelVersions(${reelId}) failed: ${err}`);
            return [];
        }
    };

    useEffect(() => {
        if (!open) return;
        [...new Set(items.map((i) => i.reelId))].forEach((id) => {
            if (!versionsByReel[id]) void loadVersions(id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, items.length]);

    const totalMs = useMemo(() => items.reduce((sum, i) => sum + (i.missing ? 0 : i.durationMs), 0), [items]);

    const move = (from: number, to: number) => {
        if (to < 0 || to >= items.length || from === to) return;
        setItems((list) => {
            const copy = [...list];
            const [moved] = copy.splice(from, 1);
            copy.splice(to, 0, moved);
            return copy;
        });
    };

    /** Version lists carry no canvas, so the duration of a newly chosen version is fetched separately. */
    const refreshDuration = async (key: string, reelId: string, version: number) => {
        try {
            const snapshot = await reelMakerApi.getReelVersion(reelId, version);
            const ms = Number((snapshot.canvas as any)?.durationMs) || 0;
            setItems((list) => list.map((i) => (i.key === key && i.version === version ? { ...i, durationMs: ms, missing: false } : i)));
        } catch (err) {
            Logger.warn(LOG_PREFIX, `duration of ${reelId} v${version} unavailable: ${err}`);
        }
    };

    const setVersion = (key: string, reelId: string, version: number) => {
        setItems((list) => list.map((i) => (i.key === key ? { ...i, version } : i)));
        void refreshDuration(key, reelId, version);
    };

    const addReels = (picked: Array<{ reel: Reel; versions: ReelVersion[] }>) => {
        setVersionsByReel((m) => {
            const copy = { ...m };
            picked.forEach((p) => {
                copy[p.reel._id] = p.versions;
            });
            return copy;
        });
        const added: DraftItem[] = picked.map(({ reel, versions }) => ({
            key: nextKey(),
            reelId: reel._id,
            reelName: reel.name,
            version: versions[0]?.version ?? reel.publishedVersion,
            durationMs: 0,
            reelStatus: reel.status,
            missing: false,
        }));
        setItems((list) => [...list, ...added]);
        added.forEach((i) => void refreshDuration(i.key, i.reelId, i.version));
    };

    const save = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setNameError('Name is required');
            return;
        }
        onSave({
            name: trimmed,
            description: description.trim(),
            items: items.map((i) => ({ itemId: i.itemId, reelId: i.reelId, version: i.version })),
        });
    };

    const previewEntries: PreviewEntry[] = items
        .filter((i) => !i.missing)
        .map((i) => ({ reelId: i.reelId, version: i.version, reelName: i.reelName, versionLabel: `v${i.version}` }));

    return (
        <>
            <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
                <DialogTitle>{playlist ? `Edit playlist · ${playlist.name}` : 'New playlist'}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {error && <Alert severity="error">{error}</Alert>}
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setNameError(null);
                            }}
                            error={Boolean(nameError)}
                            helperText={nameError || ' '}
                            required
                            fullWidth
                            disabled={!canManage}
                            inputProps={{ maxLength: 120 }}
                        />
                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            disabled={!canManage}
                            inputProps={{ maxLength: 2000 }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Reels in play order
                            </Typography>
                            <Chip size="small" label={`${items.length} reels · ${formatDuration(totalMs)}`} />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button
                                size="small"
                                variant="contained"
                                color="info"
                                startIcon={<PlayCircleOutlineIcon />}
                                onClick={() => setPreviewOpen(true)}
                                disabled={previewEntries.length === 0}
                                sx={{ textTransform: 'none' }}
                            >
                                Preview
                            </Button>
                            {canManage && (
                                <Button size="small" variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setPickerOpen(true)} sx={{ textTransform: 'none' }}>
                                    Add reels
                                </Button>
                            )}
                        </Box>

                        {items.length === 0 ? (
                            <Alert severity="info">No reels yet. Add published reels and choose which version each one plays.</Alert>
                        ) : (
                            <List dense disablePadding>
                                {items.map((item, index) => {
                                    const versions = versionsByReel[item.reelId] ?? [];
                                    const problem = item.missing
                                        ? 'This version no longer exists'
                                        : item.reelStatus === 'deleted'
                                          ? 'The reel was deleted'
                                          : item.reelStatus === 'unpublished'
                                            ? 'The reel is currently unpublished; screens keep playing this version'
                                            : '';
                                    return (
                                        <Paper
                                            key={item.key}
                                            variant="outlined"
                                            draggable={canManage}
                                            onDragStart={() => setDragKey(item.key)}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={() => {
                                                const from = items.findIndex((i) => i.key === dragKey);
                                                if (from >= 0) move(from, index);
                                                setDragKey(null);
                                            }}
                                            sx={{
                                                mb: 1,
                                                px: 1,
                                                py: 0.75,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                opacity: dragKey === item.key ? 0.5 : 1,
                                                borderColor: problem ? alpha(theme.palette.warning.main, 0.6) : undefined,
                                            }}
                                        >
                                            {canManage && <DragIndicatorIcon fontSize="small" sx={{ cursor: 'grab', color: 'text.secondary' }} />}
                                            <Typography variant="body2" sx={{ width: 28, textAlign: 'right', color: 'text.secondary' }}>
                                                {index + 1}.
                                            </Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                                    {item.reelName || item.reelId}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDuration(item.durationMs)}
                                                </Typography>
                                            </Box>
                                            {problem && (
                                                <Tooltip title={problem}>
                                                    <WarningAmberIcon fontSize="small" color="warning" />
                                                </Tooltip>
                                            )}
                                            <FormControl size="small" sx={{ minWidth: 110 }}>
                                                <InputLabel id={`ver-${item.key}`}>Version</InputLabel>
                                                <Select
                                                    labelId={`ver-${item.key}`}
                                                    label="Version"
                                                    value={item.version}
                                                    disabled={!canManage}
                                                    onOpen={() => void loadVersions(item.reelId)}
                                                    onChange={(e) => setVersion(item.key, item.reelId, Number(e.target.value))}
                                                >
                                                    {(versions.length ? versions : [{ version: item.version, label: `v${item.version}` } as ReelVersion]).map((v) => (
                                                        <MenuItem key={v.version} value={v.version}>
                                                            {v.label}
                                                            {versions[0]?.version === v.version ? ' (latest)' : ''}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {canManage && (
                                                <>
                                                    <IconButton size="small" aria-label="Move up" disabled={index === 0} onClick={() => move(index, index - 1)}>
                                                        <ArrowUpwardIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" aria-label="Move down" disabled={index === items.length - 1} onClick={() => move(index, index + 1)}>
                                                        <ArrowDownwardIcon fontSize="small" />
                                                    </IconButton>
                                                    <Tooltip title="Delete">
                                                        <Button
                                                            color="error"
                                                            variant="contained"
                                                            size="small"
                                                            aria-label="Remove"
                                                            onClick={() => setItems((list) => list.filter((i) => i.key !== item.key))}
                                                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </Button>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Paper>
                                    );
                                })}
                            </List>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" onClick={onClose} startIcon={<Cancel />} disabled={saving}>
                        {canManage ? 'Cancel' : 'Close'}
                    </Button>
                    {canManage && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={save}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                        >
                            {playlist ? 'Save' : 'Create'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <PublishedReelPicker
                open={pickerOpen}
                excludeReelIds={[]}
                onClose={() => setPickerOpen(false)}
                onPick={(picked) => {
                    addReels(picked);
                    setPickerOpen(false);
                }}
            />
            <PlaylistPreviewDialog open={previewOpen} title={name || 'Playlist'} entries={previewEntries} onClose={() => setPreviewOpen(false)} />
        </>
    );
};

interface PublishedReelPickerProps {
    open: boolean;
    excludeReelIds: string[];
    onClose: () => void;
    onPick: (picked: Array<{ reel: Reel; versions: ReelVersion[] }>) => void;
}

/** Lists published reels; the newest published version of each picked reel is added (changeable later). */
const PublishedReelPicker: React.FC<PublishedReelPickerProps> = ({ open, excludeReelIds, onClose, onPick }) => {
    const [reels, setReels] = useState<Reel[]>([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setSelected([]);
        setLoading(true);
        setError(null);
        reelMakerApi
            .listReels({ status: 'published', sortBy: 'name', sortOrder: 'asc' })
            .then((list) => setReels(list.filter((r) => r.publishedVersion > 0 && !excludeReelIds.includes(r._id))))
            .catch((err) => setError(err instanceof Error ? err.message : 'Could not load reels'))
            .finally(() => setLoading(false));
    }, [open, excludeReelIds]);

    const visible = reels.filter((r) => r.name.toLowerCase().includes(search.trim().toLowerCase()));
    const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const confirm = async () => {
        setLoading(true);
        try {
            const picked = await Promise.all(
                selected.map(async (id) => {
                    const reel = reels.find((r) => r._id === id)!;
                    const res = await reelMakerApi.listReelVersions(id);
                    return { reel, versions: res.versions };
                }),
            );
            onPick(picked.filter((p) => p.versions.length > 0));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load versions');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add published reels</DialogTitle>
            <DialogContent dividers>
                <TextField size="small" fullWidth label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }} />
                {error && <Alert severity="error">{error}</Alert>}
                {loading && reels.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                ) : visible.length === 0 ? (
                    <Alert severity="info">No published reels found. Publish a reel in Reel Maker first.</Alert>
                ) : (
                    <List dense>
                        {visible.map((r) => (
                            <ListItem key={r._id} disablePadding>
                                <ListItemButton onClick={() => toggle(r._id)}>
                                    <ListItemIcon>
                                        <Checkbox edge="start" checked={selected.includes(r._id)} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary={r.name} secondary={`${r.publishedLabel ?? ''} · ${r.width}×${r.height}`} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" startIcon={<Cancel />} onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" color="success" startIcon={<AddIcon />} disabled={selected.length === 0 || loading} onClick={() => void confirm()}>
                    Add{selected.length ? ` ${selected.length}` : ''}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlaylistEditorDialog;
